<script lang="ts">
	import { format } from 'svelte-i18n';
	//Stores
	import { readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { consumption_by_device_account } from 'src/stores/features/consumption';
	import { devicesFilters } from 'src/stores/ui/filters';
	import { navigateToDevice } from 'src/stores/ui/navigate-to';
	//SPA methods
	import { push } from 'svelte-spa-router';
	//Helpers
	import { capFirstChar, consumption_add_total, convert } from '../../helpers/utilities';
	import { load_image_from, real_id } from '../../helpers/interface_helpers';
	import { defaultSelectedRoom } from 'src/helpers/constants';
	//Design system components
	import { Text, ParameterCard, Row, Col, Stack, ListItem } from '@Shelly/shelly-design-system';
	//App components
	import NotificationList from '../Lists/NotificationList.svelte';
	import WeatherWidgetBox from '../Weather/WeatherWidgetBox.svelte';
	//Types
	import type { Device } from '../../types/Device';
	import { setSidePanelState } from '../Sidepanel/sidepanel-helpers';

	const units = 'W';
	const defaultToShow = 5;
	let showAllDevices = false;

	let devicesList = [];

	$: {
		Object.values($devices).map((d: Device) => {
			devicesList.push(d.id);
		});
	}

	$: includedPowerTotal = consumption_add_total($consumption_by_device_account, 'in_power');

	$: totalConsumption = convert(Math.abs(includedPowerTotal), units);

	// devices stats
	$: devicesCount = Object.keys($devices).length.toString();
	$: offlineDevicesCount = Object.keys($devices)
		.filter((deviceId) => !readDeviceStatus(deviceId)?.online)
		.length.toString();
	$: hiddenDevicesCount = Object.values($devices)
		.filter((d: Device) => d.room_id === -10)
		.length.toString();
	$: sharedDevicesCount = Object.values($devices)
		.filter((d: Device) => d.shared && d.room_id === -20)
		.length.toString();
	$: sharedAllDevicesCount = Object.values($devices)
		.filter((d: Device) => d.shared)
		.length.toString();

	$: consumingDevices = Object.keys($consumption_by_device_account)
		.map((devID) => {
			if (!$devices?.[devID]) {
				return {};
			}
			const { image, backgroundColor, name, icon } = $devices[devID];
			return {
				...$consumption_by_device_account?.[devID],
				image,
				backgroundColor,
				name,
				icon,
				devID,
			};
		})
		.filter((d) => {
			// filterd devices with consumption
			return d._power !== 0;
		})
		.sort((a, b) => {
			// order devices by consumption
			return b._power - a._power;
		});

	$: hideShowMoreItem = consumingDevices.length - defaultToShow <= 0;
	$: devicesToRender = showAllDevices ? consumingDevices : consumingDevices.slice(0, defaultToShow);

	const closeSidePanel = () => {
		setSidePanelState({ open: false });
	};

	const goToHidden = () => {
		push('/home/devices/-10');
		closeSidePanel();
	};

	const goToRecentlyShared = () => {
		push('/home/devices/-20');
		closeSidePanel();
	};

	const goToAllShared = () => {
		push('/home/devices/-40');
		closeSidePanel();
	};

	const goToAllDevices = () => {
		push('/home/devices/-1');
		devicesFilters.update((state) => {
			return {
				...state,
				rooms: [defaultSelectedRoom],
			};
		});
		closeSidePanel();
	};

	const goToOffline = () => {
		push('/home/devices/-30');
		closeSidePanel();
	};

	const goToConsumption = () => {
		push('/cons/-1');
		closeSidePanel();
	};
</script>

<WeatherWidgetBox />

<Text element="h2" class="mb-s">
	{capFirstChar($format('devices_stats_title', { default: 'devices stats' }))}
</Text>

<Row class="mb-m">
	<Col xs="12">
		<ParameterCard
			class="width-full cursor-pointer"
			icon={`fas fa-bolt ${includedPowerTotal < 0 ? 'text-success' : 'text-danger'}`}
			title={totalConsumption}
			description={includedPowerTotal < 0
				? $format('generating', { default: 'Generating' })
				: $format('consuming', { default: 'Consuming' })}
			on:click={goToConsumption}
		/>
		<!-- </Tooltip> -->
	</Col>
	<Col xs="6">
		<ParameterCard
			class="text-capitalize cursor-pointer"
			title={sharedAllDevicesCount}
			description={$format('label_all_shared', {
				default: 'all shared',
			})}
			on:click={() => goToAllShared()}
		/>
	</Col>
	<Col xs="6">
		<ParameterCard
			class="text-capitalize cursor-pointer"
			title={sharedDevicesCount}
			description={$format('label_recently_shared', {
				default: 'recently shared',
			})}
			on:click={goToRecentlyShared}
		/>
	</Col>

	<Col xs="6">
		<ParameterCard
			class="text-capitalize cursor-pointer"
			title={offlineDevicesCount}
			description={$format('label_offline', {
				default: 'offline',
			})}
			on:click={goToOffline}
		/>
	</Col>
	<Col xs="6">
		<ParameterCard
			class="text-capitalize cursor-pointer"
			title={hiddenDevicesCount}
			description={$format('label_hidden', { default: 'hidden' })}
			on:click={goToHidden}
		/>
	</Col>
	<Col xs="12">
		<ParameterCard
			class="text-capitalize cursor-pointer"
			title={devicesCount}
			description={$format('tab_all_devices', {
				default: 'all devices',
			})}
			on:click={goToAllDevices}
		/>
	</Col>
</Row>

<Text element="h2" class="mb-s">
	{capFirstChar(
		$format('title_actively_consuming', {
			default: 'actively consuming devices',
		}),
	)} ({consumingDevices.length})
</Text>

{#if consumingDevices.length > 0}
	<Stack gap="xs" class="mb-m">
		{#each devicesToRender as { devID, name, image, backgroundColor, in_power, _power, no_room_cons, icon }}
			<ListItem
				image={image ? load_image_from(image, $devices?.[devID]?.server) : undefined}
				{backgroundColor}
				title={name}
				progress={{
					title: convert(_power, units),
					color: in_power !== 0 ? (no_room_cons ? 'white' : 'primary') : 'white',
				}}
				viewType="default"
				{icon}
				actionBtn
				on:click={() => {
					navigateToDevice(devID);
				}}
			/>
		{/each}

		{#if !hideShowMoreItem && !showAllDevices}
			<ListItem
				viewType="more"
				title={`+${consumingDevices.length - defaultToShow} ${$format('more', {
					default: 'more',
				})}`}
				on:click={() => {
					showAllDevices = true;
				}}
			/>
		{/if}
	</Stack>
{:else}
	<Text class="mb-m" styleType="body-small">
		{$format('energy_consuming_devices_descr', {
			default: 'When devices consume energy, you will see them listed here',
		})}</Text
	>
{/if}

<NotificationList devicesFilter={devicesList} />
